import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon, type IconName } from './Icon.js';

/**
 * Menu de botão direito.
 *
 * Uma app de conversa tem mais acções do que cabem em botões visíveis, e
 * enchê-la de ícones em cada linha era pior: o menu aparece só quando se
 * pede, no sítio onde se pediu, e com o que se pode fazer ÀQUILO.
 *
 * O que o menu mostra é o que o servidor vai deixar fazer — esconder o que
 * não se pode é cortesia, não segurança. Quem constrói cada menu filtra por
 * permissões; quem decide continua a ser a API.
 */

export type MenuItem =
  | { separator: true }
  | {
      label: string;
      icon?: IconName;
      danger?: boolean;
      onSelect: () => void;
    };

export type MenuRequest = {
  x: number;
  y: number;
  title?: string;
  items: MenuItem[];
};

/** Abre um menu a partir de um evento de rato, já sem o menu do browser. */
export function menuFrom(
  event: React.MouseEvent,
  title: string,
  items: MenuItem[],
): MenuRequest {
  event.preventDefault();
  event.stopPropagation();
  return { x: event.clientX, y: event.clientY, title, items };
}

export function ContextMenu({
  request,
  onClose,
}: {
  request: MenuRequest;
  onClose: () => void;
}): React.ReactElement {
  const menu = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: request.x, y: request.y });

  // Vira-se para dentro do ecrã ANTES de pintar: medido no layout, não num
  // efeito a seguir, senão vê-se o menu saltar.
  useLayoutEffect(() => {
    const el = menu.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const margin = 8;
    setPosition({
      x: Math.min(request.x, window.innerWidth - width - margin),
      y: Math.min(request.y, window.innerHeight - height - margin),
    });
  }, [request]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    // `pointerdown` e não `click`: fechar na descida do rato é o que faz o
    // menu desaparecer antes de o clique chegar ao que está por baixo.
    const onPointer = (e: PointerEvent): void => {
      if (!menu.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer, true);
    // Um menu ancorado a um sítio que já rolou está a apontar para o nada.
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('blur', onClose);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer, true);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('blur', onClose);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  // O foco entra no menu para se poder andar com as setas e sair com Escape.
  useEffect(() => {
    menu.current?.querySelector('button')?.focus();
  }, []);

  function onKeyDown(e: React.KeyboardEvent): void {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const buttons = [...(menu.current?.querySelectorAll('button') ?? [])];
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'ArrowDown' ? at + 1 : at - 1;
    buttons[(next + buttons.length) % buttons.length]?.focus();
  }

  return (
    <div
      ref={menu}
      className="context-menu"
      role="menu"
      style={{ left: position.x, top: position.y }}
      onKeyDown={onKeyDown}
    >
      {request.title && <p className="context-title">{request.title}</p>}
      {request.items.map((item, i) =>
        'separator' in item ? (
          <hr key={`sep-${i}`} />
        ) : (
          <button
            key={item.label}
            role="menuitem"
            className={item.danger ? 'danger' : ''}
            onClick={() => {
              onClose();
              item.onSelect();
            }}
          >
            {item.icon && <Icon name={item.icon} />}
            {item.label}
          </button>
        ),
      )}
    </div>
  );
}
